"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useRouter } from "@/i18n/routing";
import { useDebounce } from "@/lib/hook/use-debounce";
import {
  type ResourceSearchResponse,
  type ResourceSearchResultItem,
} from "@/lib/fetch/resource-search";
import { RESOURCE_FAMILY_ICONS } from "@/components/resource/search/family-meta";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Search } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";

type Props = {
  initialSearch: string;
};

const ResourceSearchInput: React.FC<Props> = ({ initialSearch }) => {
  const t = useTranslations("resource");
  const [currentSearch, setCurrentSearch] = useState(initialSearch);
  const router = useRouter();

  useEffect(() => {
    setCurrentSearch(initialSearch);
  }, [initialSearch]);

  const onSearch = () => {
    const params = new URLSearchParams();

    if (currentSearch) {
      params.set("query", currentSearch);
    }

    router.push("/resource/search?" + params.toString());
  };

  return (
    <div className="relative">
      <div className="grid grid-cols-[1fr_auto_auto] gap-2">
        <div className="relative w-full">
          <Search className="absolute top-1/2 left-3 transform text-muted-foreground size-5 -translate-y-1/2" />
          <Input
            value={currentSearch}
            onChange={(e) => setCurrentSearch(e.target.value)}
            placeholder={t("search-placeholder")}
            className="pl-10 text-xs sm:text-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSearch();
              }
            }}
          />
        </div>

        <Button type="button" onClick={onSearch} className="cursor-pointer">
          <span>{t("search")}</span>
        </Button>
      </div>
      <Results query={currentSearch} />
    </div>
  );
};

export default ResourceSearchInput;

type ResultsProps = {
  query: string;
};

const Results: React.FC<ResultsProps> = ({ query }) => {
  const t = useTranslations("resource-search");
  const locale = useLocale();

  const debouncedQuery = useDebounce(query, 300);

  const { data } = useQuery({
    queryKey: ["resource", "search", "typeahead", debouncedQuery],
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      if (!debouncedQuery) return [];

      const params = new URLSearchParams({
        query: debouncedQuery,
        pageSize: "5",
        locale,
      });

      const response = await fetch(
        `/api/site/public/resource/search?${params.toString()}`,
      );

      if (!response.ok) {
        return [];
      }

      const body = (await response.json()) as {
        success: boolean;
        data: ResourceSearchResponse;
      };

      if (!body.success) {
        return [];
      }

      return body.data.items;
    },
    enabled: !!debouncedQuery,
  });

  return (
    <AnimatePresence>
      {data && data.length > 0 && (
        <motion.div
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
          className="bg-popover absolute left-0 top-12 z-30 border border-border rounded-2xl shadow-md h-fit right-0 w-full overflow-hidden"
        >
          <div>
            {data.map((item: ResourceSearchResultItem) => {
              const Icon = RESOURCE_FAMILY_ICONS[item.family];

              return (
                <Link
                  key={`${item.key}:${item.path}`}
                  href={item.path}
                  title={item.linkTitle}
                  className="grid grid-cols-[auto_1fr_40px] items-center gap-3 px-4 py-3 hover:bg-muted transition-colors"
                >
                  <span className="flex size-9 flex-none items-center justify-center rounded-xl bg-secondary">
                    <Icon className="size-4" />
                  </span>
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <p className="line-clamp-1 text-sm font-bold text-blue-700 sm:text-base">
                      {item.shortLabel}
                    </p>
                    <p className="text-muted-foreground text-xs sm:text-sm font-medium">
                      {t(`view.kinds.${item.kind}`)}
                    </p>
                  </div>
                  <Button variant="outline" className="self-center" asChild>
                    <span>
                      <ArrowUpRight className="size-6" />
                    </span>
                  </Button>
                </Link>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
